/* eslint-disable no-param-reassign */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  defaultDucts,
  defaultInfiltration
} from 'data/sizing-calculator-defaults';
import {
  BTUsType,
  GetHeatingCoolingLoadInputsType,
  RoomModelType
} from 'data/types';
import { useGetStructureHeatingCoolingLoadQuery } from 'features/api/sizing-calculator-api';
import { updateConfigurationOptionItem } from 'features/configurations';
import { setTotalBTUs, setTotalLatentGain } from 'features/project';
import { setStructureLoading, updateStructureBTUs } from 'features/structure';
import useIsSimpleSizingPanel from 'hooks/use-is-simple-sizing-panel';
import { useEffect, useMemo, useState } from 'react';
import { getBooleanAttributeValue } from 'utils/attributes';
import {
  getConditionedSpaces,
  getUnconditionedSpaces
} from 'utils/conditioned-spaces';
import {
  unconditionedSpacesWithLoads,
  zeroBtusBySpaceId
} from 'utils/unconditioned-space-loads';
import {
  deriveDefaultDucts,
  resolveBlowerHeatDiscount,
  toCanonicalDuctSystemType
} from 'utils/duct-defaults';
import { deriveHybridDuctedSpaceIds } from 'utils/hybrid-config-autobuild';
import {
  getProjectSettingValue,
  isDuctedPathway,
  isHybridOnlySystemPreference
} from 'utils/project';

export default function BTUWatcher() {
  const dispatch = useAppDispatch();
  const { locationData, projectSettings } = useAppSelector(
    (state) => state.project
  );
  const { configurationOptions, configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const { structure, structureUniqueId, existingSystems, autoLayoutSpaceIds } =
    useAppSelector((state) => state.structure);
  const { items } = useAppSelector((state) => state.data);

  const [sizingInput, setSizingInput] =
    useState<GetHeatingCoolingLoadInputsType | null>(null);
  // Run A of the hybrid duct-loss overlay (AHA-6784 design B): same input
  // as run B except derived (lossy) ducts and NO structureUniqueId, so
  // ss-api skips persistence. null outside hybrid.
  const [hybridDuctInput, setHybridDuctInput] =
    useState<GetHeatingCoolingLoadInputsType | null>(null);
  const spacesToSize = getConditionedSpaces(structure);
  const hasSpaces = spacesToSize.length > 0;
  const hasSizableStructure = spacesToSize.some((space) => {
    const sa = space.sizingAttributes;
    if (!sa) return false;
    return (
      (sa.aboveGradeWalls?.length ?? 0) > 0 ||
      (sa.belowGradeWalls?.length ?? 0) > 0 ||
      (sa.ceilingsBelowAttic?.length ?? 0) > 0 ||
      (sa.ceilingsBelowRoof?.length ?? 0) > 0 ||
      (sa.floorsOverUnconditionedSpace?.length ?? 0) > 0 ||
      (sa.floorsOverExposedSpace?.length ?? 0) > 0 ||
      (sa.slabFloors?.length ?? 0) > 0 ||
      (sa.basementFloors?.length ?? 0) > 0
    );
  });
  const canSize = hasSpaces && hasSizableStructure && !!structureUniqueId;
  const isSimpleSizingPanel = useIsSimpleSizingPanel();
  const squareFootage = getProjectSettingValue('square_footage');
  const canRequestSizing = canSize && !!locationData && !!squareFootage;

  const {
    data: sizingData,
    isFetching: sizingLoading,
    error: sizingError
  } = useGetStructureHeatingCoolingLoadQuery(sizingInput, {
    skip: !canSize || isSimpleSizingPanel,
    refetchOnMountOrArgChange: true
  });

  const isHybrid = isHybridOnlySystemPreference(
    projectSettings.find((ps) => ps.code === 'system_preference')?.value
  );

  // Run A query — hybrid only (hybridDuctInput stays null elsewhere, and
  // the null skip also avoids a null-body POST before the input effect
  // lands).
  const {
    data: hybridDuctData,
    isFetching: hybridDuctLoading,
    error: hybridDuctError
  } = useGetStructureHeatingCoolingLoadQuery(hybridDuctInput, {
    skip: !canSize || !hybridDuctInput || isSimpleSizingPanel,
    refetchOnMountOrArgChange: true
  });

  const hasBlowerHeatDiscount = getProjectSettingValue(
    'has_blower_heat_discount'
  );

  // Membership of the ducted zone: the active configuration's ducted zones
  // once it exists; before it is built (earlier structure-page steps), the
  // exact spaces hybrid auto-build will put there — so totals never jump
  // when the configuration materializes. Serialized because the merge
  // dispatch below rewrites structure/space objects — keying the delta
  // memo on raw references would loop the effect.
  const resolveDuctedSpaceIds = (): string[] => {
    if (!isHybrid) return [];
    const activeConfiguration = configurations[activeConfig];
    if (activeConfiguration) {
      return activeConfiguration.condensers.flatMap((condenser) =>
        condenser.zones
          .filter((zone) => zone.type === 'ducted')
          .flatMap((zone) =>
            zone.zoneItems.map((zoneItem) => zoneItem.structureItemId)
          )
      );
    }
    return deriveHybridDuctedSpaceIds(structure, autoLayoutSpaceIds);
  };
  const ductedSpaceIdsKey = JSON.stringify(resolveDuctedSpaceIds().sort());

  // Per-space duct-loss delta = run A load minus run A subtotal — exactly
  // rawSubtotal × ductFactor × multiplier (increase-free by construction:
  // run A has no structureUniqueId so ss-api adds no per-space increases,
  // and identical blower inputs cancel between runs). Latent falls back to
  // 0 when a room's latent subtotal is absent.
  const hybridDeltas = useMemo(() => {
    if (!isHybrid || !hybridDuctData) return null;
    const ductedSpaceIds = new Set<string>(JSON.parse(ductedSpaceIdsKey));
    const perSpace: {
      [uniqueId: string]: { heating: number; cooling: number; latent: number };
    } = {};
    let heatingSum = 0;
    let coolingSum = 0;
    let latentSum = 0;
    hybridDuctData.roomLoads.forEach((roomLoad) => {
      if (!roomLoad.roomId || !ductedSpaceIds.has(roomLoad.roomId)) return;
      const heating = roomLoad.heatingLoad - roomLoad.heatingLoadSubTotal;
      const cooling = roomLoad.coolingLoad - roomLoad.coolingLoadSubTotal;
      const latent =
        roomLoad.latentLoad != null && roomLoad.latentLoadSubTotal != null
          ? roomLoad.latentLoad - roomLoad.latentLoadSubTotal
          : 0;
      perSpace[roomLoad.roomId] = { heating, cooling, latent };
      heatingSum += heating;
      coolingSum += cooling;
      latentSum += latent;
    });
    return { perSpace, heatingSum, coolingSum, latentSum };
  }, [isHybrid, hybridDuctData, ductedSpaceIdsKey]);

  // Run B must not be applied on hybrid until the run A overlay lands
  // (or run A errors — then zero-loss fallback beats blocking forever).
  // Pending uses the same location/sqft gate as run A creation: without
  // those, the overlay run never starts, and stalling would block loads.
  const hybridOverlayPending =
    isHybrid && canRequestSizing && !hybridDeltas && !hybridDuctError;

  useEffect(() => {
    if (isSimpleSizingPanel) {
      dispatch(setStructureLoading(false));
      return;
    }
    dispatch(setStructureLoading(sizingLoading || hybridDuctLoading));
  }, [sizingLoading, hybridDuctLoading, isSimpleSizingPanel]);

  useEffect(() => {
    if (isSimpleSizingPanel) return;
    if (sizingData) {
      if (hybridOverlayPending) return;
      const newSpaceBTUs: { [uniqueId: string]: BTUsType } = {};
      sizingData.roomLoads.forEach((roomLoad) => {
        if (roomLoad.roomId) {
          const delta = hybridDeltas?.perSpace[roomLoad.roomId];
          newSpaceBTUs[roomLoad.roomId] = {
            cooling: roomLoad.coolingLoad + (delta?.cooling ?? 0),
            heating: roomLoad.heatingLoad + (delta?.heating ?? 0),
            latent: (roomLoad.latentLoad ?? 0) + (delta?.latent ?? 0)
          };
        }
      });

      getUnconditionedSpaces(structure).forEach((space) => {
        newSpaceBTUs[space.uniqueId] = {
          cooling: 0,
          heating: 0,
          latent: 0
        };
      });

      dispatch(updateStructureBTUs(newSpaceBTUs));
      dispatch(
        setTotalLatentGain(
          sizingData.latentGain + (hybridDeltas?.latentSum ?? 0)
        )
      );
    }
  }, [
    sizingData,
    sizingError,
    hybridDeltas,
    hybridOverlayPending,
    isSimpleSizingPanel
  ]);

  const unconditionedSpaceIdsKey = getUnconditionedSpaces(structure)
    .map((space) => space.uniqueId)
    .sort()
    .join(',');

  useEffect(() => {
    const stale = unconditionedSpacesWithLoads(
      getUnconditionedSpaces(structure)
    );
    if (stale.length === 0) return;
    dispatch(updateStructureBTUs(zeroBtusBySpaceId(stale)));
  }, [unconditionedSpaceIdsKey, structure, dispatch]);

  useEffect(() => {
    if (isSimpleSizingPanel || !canSize) {
      setSizingInput(null);
      setHybridDuctInput(null);
      return;
    }
    if (!locationData || !squareFootage || !structureUniqueId) {
      setHybridDuctInput(null);
      return;
    }
    const rooms: RoomModelType[] = spacesToSize.map((space) => {
      const roomId = space.uniqueId;
      const rawAppliances = space.sizingAttributes?.appliances || [];
      const appliances = rawAppliances.filter(
        (a) =>
          typeof a.wattage === 'number' &&
          Number.isFinite(a.wattage) &&
          typeof a.inUseOutput === 'number' &&
          Number.isFinite(a.inUseOutput) &&
          typeof a.perHourUsage === 'number' &&
          Number.isFinite(a.perHourUsage)
      );
      return {
        roomId,
        walls: {
          aboveGradeWalls: space.sizingAttributes?.aboveGradeWalls || [],
          belowGradeWalls: space.sizingAttributes?.belowGradeWalls || []
        },
        ceilings: {
          ceilingsBelowAttic: space.sizingAttributes?.ceilingsBelowAttic || [],
          ceilingsBelowRoof: space.sizingAttributes?.ceilingsBelowRoof || []
        },
        floors: {
          floorsOverUnconditionedSpace:
            space.sizingAttributes?.floorsOverUnconditionedSpace || [],
          floorsOverExposedSpace:
            space.sizingAttributes?.floorsOverExposedSpace || [],
          slabFloors: space.sizingAttributes?.slabFloors || [],
          basementFloors: space.sizingAttributes?.basementFloors || []
        },
        appliances,
        isBedroom: getBooleanAttributeValue(space.attributes, 'is_bedroom'),
        isMainBedroom: getBooleanAttributeValue(
          space.attributes,
          'is_main_bedroom'
        )
      };
    });

    const validLevels = structure.filter((level) => level.type === 'level');
    const validLevelsWithSpaces = validLevels.filter((level) =>
      level.sections.some((section) =>
        section.spaces.some(
          (space) => space.BTUs?.heating || space.BTUs?.cooling
        )
      )
    );
    const constructionType =
      validLevelsWithSpaces.length < 2
        ? 'Single Story House'
        : 'Two Story House';

    // Derived realistic duct defaults on the ducted pathway; ductless
    // keeps defaultDucts ('Ducts in Conditioned Space' -> zero duct
    // loss/gain, correct for ductless). Imperative isDuctedPathway() is
    // safe here: projectSettings is already an effect dep. Hybrid keeps
    // the ductless arm HERE (run B, byte-stable and persisting) and gets
    // its ducted-zone duct loss from the run A delta overlay below.
    // On hybrid, explicit duct_* project settings are DELIBERATELY
    // excluded from run B: the run A delta overlay already carries them,
    // and the overlay invariant is run B = zero-loss — letting explicit
    // settings reach run B would double-count duct loss in ducted-zone
    // spaces and leak house-wide loss into ductless spaces.
    const derivedDucts = isDuctedPathway()
      ? deriveDefaultDucts(structure, existingSystems, projectSettings)
      : defaultDucts;

    const newSizingInput: GetHeatingCoolingLoadInputsType = {
      structureUniqueId,
      structureSquareFootage: squareFootage,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      indoorDesignHeatingTemperature: getProjectSettingValue('desired_heating'),
      indoorDesignCoolingTemperature: getProjectSettingValue('desired_cooling'),
      infiltration: {
        ...defaultInfiltration,
        numberOfFireplaces: getProjectSettingValue('number_of_fireplaces') ?? 0,
        constructionType
      },
      blower: {
        hasBlowerHeatDiscount: resolveBlowerHeatDiscount(
          hasBlowerHeatDiscount,
          isDuctedPathway()
        )
      },
      ducts: isHybrid
        ? {
            // Pinned to zero-loss defaults verbatim — see the comment on
            // derivedDucts above.
            ductSystemType: toCanonicalDuctSystemType(
              defaultDucts.ductSystemType
            ),
            ductWallInsulation: defaultDucts.ductWallInsulation,
            ductLeakageClass: defaultDucts.ductLeakageClass
          }
        : {
            // The canonical mapping wraps the EFFECTIVE value so a stored
            // 7D-AE selection (single-space attribute answer) can never
            // reach pnet-api's verbatim ManualJ Table7Description match.
            ductSystemType: toCanonicalDuctSystemType(
              getProjectSettingValue('duct_system_type') ||
                derivedDucts.ductSystemType
            ),
            ductWallInsulation:
              getProjectSettingValue('duct_wall_insulation') ||
              derivedDucts.ductWallInsulation,
            ductLeakageClass:
              getProjectSettingValue('duct_leakage_class') ||
              derivedDucts.ductLeakageClass
          },
      rooms,
      includeEngineeredVentilation: false,
      ventilation: {},
      numberOfSmallPlants: 0,
      numberOfMediumPlants: 0,
      numberOfLargePlants: 0
    };

    setSizingInput(newSizingInput);

    if (isHybrid) {
      // Run A: identical to run B (blower included — the delta cancels
      // it) except the ducted-path duct field logic and NO
      // structureUniqueId (ss-api then skips persistence and per-space
      // additionalBTUs). Known limitation, per design: the unset blower
      // default keeps the ductless resolution in both runs, so
      // ducted-zone blower heat is omitted unless the user answers the
      // Discount Blower Heat Gain control explicitly.
      const { structureUniqueId: omitted, ...sharedInput } = newSizingInput;
      const hybridDerivedDucts = deriveDefaultDucts(
        structure,
        existingSystems,
        projectSettings
      );
      setHybridDuctInput({
        ...sharedInput,
        ducts: {
          ductSystemType: toCanonicalDuctSystemType(
            getProjectSettingValue('duct_system_type') ||
              hybridDerivedDucts.ductSystemType
          ),
          ductWallInsulation:
            getProjectSettingValue('duct_wall_insulation') ||
            hybridDerivedDucts.ductWallInsulation,
          ductLeakageClass:
            getProjectSettingValue('duct_leakage_class') ||
            hybridDerivedDucts.ductLeakageClass
        }
      });
    } else {
      setHybridDuctInput(null);
    }
  }, [
    locationData,
    structure,
    structureUniqueId,
    existingSystems,
    items,
    projectSettings,
    hasBlowerHeatDiscount,
    squareFootage,
    canSize,
    isHybrid,
    isSimpleSizingPanel
  ]);

  useEffect(() => {
    if (isSimpleSizingPanel) return;
    if (!canSize) {
      dispatch(setTotalBTUs({ heating: 0, cooling: 0 }));
      return;
    }
    if (hybridOverlayPending) return;

    // Totals = run B block totals + the ducted-space delta sums (preserves
    // run B's block-only components; ductless spaces contribute nothing).
    dispatch(
      setTotalBTUs({
        heating:
          (sizingData?.heatingLoad || 0) + (hybridDeltas?.heatingSum ?? 0),
        cooling:
          (sizingData?.coolingLoad || 0) +
          (sizingData?.latentGain || 0) +
          (hybridDeltas?.coolingSum ?? 0) +
          (hybridDeltas?.latentSum ?? 0)
      })
    );
  }, [
    structure,
    sizingData,
    hybridDeltas,
    hybridOverlayPending,
    isSimpleSizingPanel
  ]);

  useEffect(() => {
    configurationOptions.forEach((config) => {
      config.condensers.forEach((condenser) => {
        condenser.zones.forEach((zone) => {
          zone.zoneItems.forEach((zoneItem) => {
            const partitionCount = config.condensers
              .flatMap((c) => c.zones.flatMap((z) => z.zoneItems))
              .filter(
                (zi) => zi.structureItemId === zoneItem.structureItemId
              ).length;
            const newZoneItemBTUs = {
              heating: zoneItem.originalBTUs.heating / partitionCount,
              cooling: zoneItem.originalBTUs.cooling / partitionCount
            };

            dispatch(
              updateConfigurationOptionItem({
                uniqueId: zoneItem.uniqueId,
                BTUs: newZoneItemBTUs,
                isPartition: partitionCount > 1
              })
            );
          });
        });
      });
    });
  }, [
    JSON.stringify(
      configurationOptions.map((config) => ({
        uniqueId: config.uniqueId,
        condensers: config.condensers.map((cond) => ({
          uniqueId: cond.uniqueId,
          zones: cond.zones.map((zone) => ({
            uniqueId: zone.uniqueId,
            zoneItems: zone.zoneItems.map((item) => ({
              uniqueId: item.uniqueId
            }))
          }))
        }))
      }))
    )
  ]);

  return null;
}
